import { BenefitTypeOptions } from '@strata/constants';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { GridWithFilters, PrimaryData, SecondaryData, TimeFrameOptions } from '@strata/react-grid';
import { MultiIssueDetail } from '@strata/react-status-icons';
import { OpportunityStatusIndicator } from '@strata/react-status-indicators';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { OpportunityRowMenu } from 'components/opportunities/OpportunityRowMenu';
import { mapOpportunityRow } from 'services/opportunityHelpers';
import { AppRoutes } from 'services/routes';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';
import { fetchOpportunities, resetAllPages } from 'store/opportunities/opportunitiesSlice';

const initialSortProperties = [{ id: 'receivedDate', desc: true }];

const timeFrameConfig = {
    key: 'timeFrame',
    title: 'Opportunity Time Frame',
    defaultValue: TimeFrameOptions.PAST_MONTH,
    optionOverrideMap: {
        [TimeFrameOptions.PAST_DAY]: { text: 'Past 24 Hours' },
        ALL_TIME: { text: 'All Time', handler: () => true },
    },
};

const columns = [
    {
        accessorKey: 'patientName',
        header: 'Patient',
        size: 130,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        accessorKey: 'patientDOB',
        header: 'DOB',
        size: 110,
    },
    {
        accessorKey: 'memberId',
        header: 'Member ID',
        size: 120,
    },
    {
        accessorKey: 'authId',
        header: 'Authorization ID',
        size: 140,
    },
    {
        accessorKey: 'productServiceName',
        header: 'Product',
        size: 110,
    },
    {
        accessorKey: 'segmentName',
        header: 'Health Plan',
        size: 110,
    },
    {
        accessorKey: 'receivedDate',
        header: 'Received Date',
        size: 110,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        enableSorting: false,
        cell: ({ getValue }) => <OpportunityStatusIndicator status={getValue()} />,
    },
    {
        accessorKey: 'issues',
        header: 'Status Details',
        size: 145,
        enableSorting: false,
        cell: ({ getValue }) => <MultiIssueDetail issues={getValue()} />,
    },
    {
        accessorKey: 'treatmentId',
        header: '',
        size: 50,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <OpportunityRowMenu treatmentId={getValue()} row={row.original} />
        ),
    },
];

export const OpportunitiesListPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { data: segments, isLoading: segmentsLoading } = useGetPayerSegmentsQuery();
    const { payerSegmentList } = segments ?? {};

    const timeFrameFilter = useSelector(
        (state) =>
            state.grids.opportunities.activeFilters?.timeFrame?.[0] ?? TimeFrameOptions.PAST_MONTH,
    );

    const filterCategories = useMemo(
        () => [
            {
                key: 'segment_id',
                title: 'Health Plan',
                customValues: payerSegmentList?.map((segment) => ({
                    value: segment.segment_id,
                    text: segment.segment_name,
                })),
            },
            {
                key: 'status',
                title: 'Status',
                customValues: Object.entries(OpportunityStatusIndicator.StatusMessages)
                    .sort(([, a], [, b]) => sortAsc(a, b))
                    .map(([key, value]) => ({
                        value:
                            key === OpportunityStatusIndicator.Statuses.INCOMPLETE
                                ? 'OPPORTUNITY'
                                : key,
                        text: value,
                    })),
            },
            {
                key: 'benefit',
                title: 'Benefit',
                customValues: BenefitTypeOptions.map(({ name, value }) => ({
                    text: name,
                    value,
                })),
            },
            {
                key: 'is_brand',
                title: 'Product Type',
                customValues: [
                    {
                        value: '1',
                        text: 'Brand',
                    },
                    {
                        value: '0',
                        text: 'Generic',
                    },
                ],
            },
        ],
        [payerSegmentList],
    );

    const {
        activeFilters,
        filterPaneWidth,
        page,
        search,
        sort,
        updateCurrentPage,
        updateFilterPaneWidth,
        updateGridActiveFilters,
        updateGridSearch,
        updateGridSort,
    } = useGridState(Grids.OPPORTUNITIES, initialSortProperties);

    const { pages, rowCount } = useSelector((state) => state.opportunities.pageData);

    const opportunities = useMemo(() => (pages[page] ?? []).map(mapOpportunityRow), [page, pages]);

    const [searching, setSearching] = useState(false);
    useEffect(() => {
        const handler = setTimeout(() => {
            if (searching) {
                setSearching(false);
                dispatch(resetAllPages());
            }
        }, 1000);
        return () => clearTimeout(handler);
    }, [search, searching, dispatch]);

    useEffect(() => {
        if (!pages[page]) {
            dispatch(fetchOpportunities(page, timeFrameFilter, search));
        }
    }, [page, dispatch, pages, search, timeFrameFilter]);

    return (
        <FormPageContainer>
            <GridWithFilters
                data={opportunities}
                isLoading={!pages[page] || searching || segmentsLoading}
                manualSorting
                sortProperties={sort}
                onSort={(sortDetails) => {
                    dispatch(resetAllPages());
                    updateGridSort(sortDetails);
                }}
                enableSearch
                searchText={search}
                onSearch={(searchText) => {
                    setSearching(true);
                    updateGridSearch(searchText);
                }}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={(filters) => {
                    setSearching(true);
                    updateGridActiveFilters(filters);
                }}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                timeFrameConfig={timeFrameConfig}
                currentPage={page}
                totalCount={rowCount}
                onPageChange={updateCurrentPage}
                onRowClick={(rowData) => navigate(AppRoutes.opportunityDetails(rowData.referralId))}
                columns={columns}
            />
        </FormPageContainer>
    );
};
